import { createRootRoute, createRoute, createRouter, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { KeyRound, ShieldAlert } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { setAuthTransport } from "@/api/client";
import { env } from "@/app/env";
import { queryClient } from "@/app/query-client";
import { AuthProvider } from "@/auth/auth-provider";
import { isAdmin, isOperator, isReleaseApprover, operatorRouteAllowed } from "@/auth/capabilities";
import type { LicensingRole } from "@/auth/token-session";
import { useAuth } from "@/auth/use-auth";
import { AdminShell } from "@/components/layout/admin-shell";
import { AuthGate } from "@/components/layout/auth-gate";
import { LoadingState } from "@/components/layout/route-states";
import { Button } from "@/components/ui/button";
import { AppsPage } from "@/features/apps/apps-page";
import { AuditPage } from "@/features/audit/audit-page";
import { EnrollmentsPage } from "@/features/enrollments/enrollments-page";
import { HomePage } from "@/features/home/home-page";
import { ClientLicensingPage } from "@/features/licensing/client-licensing-page";
import { ClientDirectoryPage } from "@/features/licensing/client-directory-page";
import { ProductActivationsPage } from "@/features/licensing/product-activations-page";
import { PublicDownloadPage } from "@/features/public-download/public-download-page";
import { RegistrationRequestDetailPage } from "@/features/registration-requests/registration-request-detail-page";
import { RegistrationRequestLookupPage } from "@/features/registration-requests/registration-request-lookup-page";
import { ReportsPage } from "@/features/reports/reports-page";
import { ReleasePublicationPage } from "@/features/release-publication/release-publication-page";
import { SettingsPage } from "@/features/settings/settings-page";
import { StationDetailPage } from "@/features/stations/station-detail-page";
import { StationsPage } from "@/features/stations/stations-page";
import { positiveIntegerSearch } from "@/lib/search";

const routerBasepath = import.meta.env.BASE_URL === "/" ? "/" : import.meta.env.BASE_URL.replace(/\/$/, "");

const rootRoute = createRootRoute({
  component: RootLayout
});

function RootLayout() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  if (isPublicRoute(pathname)) {
    return <PublicRouteLayout />;
  }
  if (pathname === "/login") {
    return (
      <AuthProvider>
        <Outlet />
      </AuthProvider>
    );
  }
  return (
    <AuthProvider>
      <TokenBridge />
      <AuthGate>
		<RoleRouteGate>
		  <AdminShell />
		</RoleRouteGate>
      </AuthGate>
    </AuthProvider>
  );
}

function PublicRouteLayout() {
  useEffect(() => {
    const callbackParams = new URLSearchParams(window.location.hash.replace(/^#/, ""));
    if (callbackParams.has("state") && callbackParams.has("code")) {
      window.history.replaceState(window.history.state, document.title, `${window.location.pathname}${window.location.search}`);
    }
  }, []);

  return <Outlet />;
}

function isPublicRoute(pathname: string) {
  return pathname === "/" || pathname.startsWith("/download");
}

function TokenBridge() {
  const auth = useAuth();

  useEffect(() => {
    setAuthTransport({
      getSnapshot: auth.getTokenSnapshot,
      currentSnapshot: auth.currentTokenSnapshot,
      onTerminalUnauthorized: (rejected) => {
        queryClient.clear();
        auth.onTerminalUnauthorized(rejected);
      }
    });
    return () => setAuthTransport(null);
  }, [auth.currentTokenSnapshot, auth.getTokenSnapshot, auth.onTerminalUnauthorized]);

  return null;
}

function RoleRouteGate({ children }: { children: ReactNode }) {
	const auth = useAuth();
	const location = useRouterState({ select: (state) => state.location });
	const { t } = useTranslation("auth");
	if (env.uiMode === "catalog") {
	  if ((isAdmin(auth.roles) || isReleaseApprover(auth.roles)) && catalogRouteAllowed(location.pathname, auth.roles)) {
		return children;
	  }
	  return <LoginMessage title={t("forbidden")} detail={t("forbiddenDetail")} />;
	}
	if (env.uiMode === "tenant") {
	  if (isAdmin(auth.roles) && tenantRouteAllowed(location.pathname)) {
		return children;
	  }
	  if (isOperator(auth.roles) && operatorRouteAllowed(location.pathname, location.searchStr)) {
		return children;
	  }
	  return <LoginMessage title={t("forbidden")} detail={t("forbiddenDetail")} />;
	}
	if (isAdmin(auth.roles)) return children;
	if (isOperator(auth.roles) && operatorRouteAllowed(location.pathname, location.searchStr)) {
	  return children;
	}
	if (env.uiMode === "legacy" && isReleaseApprover(auth.roles) && location.pathname === "/release-publication-requests") {
	  return children;
	}
	return <LoginMessage title={t("forbidden")} detail={t("forbiddenDetail")} />;
}

function catalogRouteAllowed(pathname: string, roles: ReadonlySet<LicensingRole>) {
	if (pathname === "/release-publication-requests") return isReleaseApprover(roles);
	return isAdmin(roles) && (pathname === "/admin" || pathname === "/apps" || pathname === "/settings");
}

function tenantRouteAllowed(pathname: string) {
	return pathname === "/admin" ||
	  pathname === "/overview" ||
	  pathname === "/clients" || pathname.startsWith("/clients/") ||
	  pathname === "/registration-requests" || pathname.startsWith("/registration-requests/") ||
	  pathname === "/product-activations" ||
	  pathname === "/enrollments" ||
	  pathname === "/stations" || pathname.startsWith("/stations/") ||
	  pathname === "/audit" ||
	  pathname === "/reports";
}

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/",
  component: PublicDownloadPage
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/login",
  component: LoginPage
});

const downloadRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/download",
  component: PublicDownloadPage
});

const appsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/apps",
  component: AppsPage
});

const catalogAdminRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/admin",
  component: AdminEntry
});

const enrollmentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/enrollments",
  validateSearch: (search: Record<string, unknown>) => ({
    appId: typeof search.appId === "string" ? search.appId : undefined,
    clientId: positiveIntegerSearch(search.clientId),
    status: search.status === "UNUSED" || search.status === "USED" || search.status === "EXPIRED" ? search.status : undefined
  }),
  component: EnrollmentsPage
});

const registrationRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/registration-requests",
  component: RegistrationRequestLookupPage
});

const productActivationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/product-activations",
  validateSearch: (search: Record<string, unknown>) => ({
    clientId: positiveIntegerSearch(search.clientId)
  }),
  component: ProductActivationsPage
});

const clientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/clients",
  validateSearch: (search: Record<string, unknown>) => ({
    q: typeof search.q === "string" ? search.q.slice(0, 160) : undefined,
    anchor: typeof search.anchor === "string" && /^[1-9]\d*$/.test(search.anchor) ? search.anchor : undefined,
    view: search.view === "all" ? "all" as const : undefined
  }),
  component: ClientDirectoryPage
});

const clientLicensingRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/clients/$clientId",
  validateSearch: (search: Record<string, unknown>) => ({
    fromQ: typeof search.fromQ === "string" ? search.fromQ.slice(0, 160) : undefined,
    fromClientId: typeof search.fromClientId === "string" && /^[1-9]\d*$/.test(search.fromClientId) ? search.fromClientId : undefined,
    fromView: search.fromView === "all" ? "all" as const : undefined
  }),
  component: ClientLicensingPage
});

const releasePublicationRequestsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/release-publication-requests",
  component: ReleasePublicationPage
});

const registrationRequestDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/registration-requests/$requestId",
  component: RegistrationRequestDetailPage
});

const stationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/stations",
  validateSearch: (search: Record<string, unknown>) => ({
    appId: typeof search.appId === "string" ? search.appId : undefined,
    clientId: positiveIntegerSearch(search.clientId),
    status: typeof search.status === "string" ? search.status : undefined,
    activeOnly: typeof search.activeOnly === "boolean" ? search.activeOnly : undefined,
    outdatedOnly: typeof search.outdatedOnly === "boolean" ? search.outdatedOnly : undefined,
    sort: typeof search.sort === "string" ? search.sort : undefined,
    page: typeof search.page === "string" ? search.page : undefined,
    pageSize: typeof search.pageSize === "string" ? search.pageSize : undefined
  }),
  component: StationsPage
});

const stationDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/stations/$stationId",
	validateSearch: (search: Record<string, unknown>) => ({
	  recoveryRequestId: typeof search.recoveryRequestId === "string" ? search.recoveryRequestId : undefined
	}),
  component: StationDetailPage
});

const auditRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/audit",
  validateSearch: (search: Record<string, unknown>) => ({
    event: typeof search.event === "string" ? search.event : undefined,
    stationId: typeof search.stationId === "string" ? search.stationId : undefined,
    appId: typeof search.appId === "string" ? search.appId : undefined,
    clientId: positiveIntegerSearch(search.clientId),
    actor: typeof search.actor === "string" ? search.actor : undefined,
    from: typeof search.from === "string" ? search.from : typeof search.since === "string" ? search.since : undefined,
    to: typeof search.to === "string" ? search.to : typeof search.until === "string" ? search.until : undefined,
    sort: search.sort === "at_asc" || search.sort === "at_desc" ? search.sort : undefined,
    page: typeof search.page === "string" ? search.page : undefined,
    pageSize: typeof search.pageSize === "string" ? search.pageSize : undefined
  }),
  component: AuditPage
});

const reportsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/reports",
  validateSearch: (search: Record<string, unknown>) => ({
    appId: typeof search.appId === "string" ? search.appId : undefined,
    clientId: positiveIntegerSearch(search.clientId),
    staleAfterHours: typeof search.staleAfterHours === "string" ? search.staleAfterHours : undefined,
    since: typeof search.since === "string" ? search.since : undefined,
    until: typeof search.until === "string" ? search.until : undefined
  }),
  component: ReportsPage
});

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/settings",
  component: SettingsPage
});

const homeRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/overview",
  component: HomePage
});

function LoginPage() {
  const auth = useAuth();
  const navigate = useNavigate({ from: "/login" });
  const loginStarted = useRef(false);
  const { t } = useTranslation("auth");
  const common = useTranslation("common").t;

  useEffect(() => {
	if (auth.status === "authenticated") {
	  if (env.uiMode === "catalog") {
		void navigate({ to: isAdmin(auth.roles) ? "/apps" : "/release-publication-requests", replace: true });
	  } else {
		void navigate({ to: isAdmin(auth.roles) ? "/clients" : "/registration-requests", replace: true });
	  }
    }
    if (auth.status === "anonymous" && !loginStarted.current) {
      loginStarted.current = true;
      auth.login("/login");
    }
  }, [auth, navigate]);

  if (auth.status === "forbidden") {
    return (
      <LoginMessage
        title={t("forbidden")}
        detail={t("forbiddenDetail")}
        action={
          <Button variant="outline" onClick={auth.logout}>
            {common("actions.signOut")}
          </Button>
        }
      />
    );
  }

  if (auth.status === "unavailable") {
    return <LoginMessage title={t("unavailable")} detail={t("unavailableDetail")} />;
  }

  return (
    <div className="grid min-h-screen place-items-center bg-background p-6">
      <div className="w-full max-w-md rounded-md border bg-panel p-6 text-panel-foreground shadow-sm">
        <KeyRound className="h-8 w-8 text-primary" />
        <h1 className="mt-4 text-xl font-semibold">{t("signingIn")}</h1>
        <div className="mt-5">
          <LoadingState />
        </div>
      </div>
    </div>
  );
}

function AdminEntry() {
  const navigate = useNavigate();
  useEffect(() => {
    void navigate({ to: env.uiMode === "catalog" ? "/apps" : "/clients", replace: true });
  }, [navigate]);
  return <LoadingState />;
}

function LoginMessage({ title, detail, action }: { title: string; detail: string; action?: ReactNode }) {
  return (
    <div className="grid min-h-screen place-items-center bg-background p-6">
      <div className="w-full max-w-md rounded-md border bg-panel p-6 text-panel-foreground shadow-sm">
        <ShieldAlert className="h-8 w-8 text-primary" />
        <h1 className="mt-4 text-xl font-semibold">{title}</h1>
        <p className="mt-2 text-sm text-muted-foreground">{detail}</p>
        {action ? <div className="mt-5">{action}</div> : null}
      </div>
    </div>
  );
}

const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  downloadRoute,
  catalogAdminRoute,
  appsRoute,
  enrollmentsRoute,
  registrationRequestsRoute,
  productActivationsRoute,
  clientsRoute,
  clientLicensingRoute,
  releasePublicationRequestsRoute,
  registrationRequestDetailRoute,
  stationsRoute,
  stationDetailRoute,
  auditRoute,
  reportsRoute,
  settingsRoute,
  homeRoute
]);

export const router = createRouter({
  routeTree,
  basepath: routerBasepath
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
